import { useNavigate, useSearchParams } from 'react-router-dom';
import { LogOut, Leaf, Camera, ChevronRight, User, Download, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { EditNameDialog } from '@/components/profile/EditNameDialog';
import { DeleteAccountDialog } from '@/components/profile/DeleteAccountDialog';
import { SubscriptionCard } from '@/components/subscription/SubscriptionCard';
import { UsageIndicator } from '@/components/subscription/UsageIndicator';
import { UpgradeDialog } from '@/components/subscription/UpgradeDialog';
import { useSubscription } from '@/hooks/useSubscription';
import { NotificationToggle } from '@/components/profile/NotificationToggle';
import { trackPremiumConversion } from '@/lib/gtag';

export default function Profile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, signOut } = useAuth();
  const [plantCount, setPlantCount] = useState(0);
  const [isInstalled, setIsInstalled] = useState(false);
  const [profile, setProfile] = useState<{ display_name: string | null; phone: string | null } | null>(null);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);

  const { 
    plan, 
    monthlyUsage, 
    plantCount: subPlantCount, 
    subscriptionEnd, 
    loading: subscriptionLoading,
    refreshSubscription,
    limits 
  } = useSubscription();

  useEffect(() => {
    if (user) {
      fetchPlantCount();
      fetchProfile();
    }
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }
  }, [user]);

  // Check for checkout success/cancel
  useEffect(() => {
    const checkoutStatus = searchParams.get('checkout');
    if (checkoutStatus === 'success') {
      trackPremiumConversion();
      toast.success('Assinatura realizada com sucesso! Bem-vindo ao Premium!');
      refreshSubscription();
      // Clean URL
      window.history.replaceState({}, '', '/profile');
    } else if (checkoutStatus === 'canceled') {
      toast.info('Checkout cancelado');
      window.history.replaceState({}, '', '/profile');
    }
  }, [searchParams, refreshSubscription]);

  const fetchPlantCount = async () => {
    const { count } = await supabase
      .from('plant_identifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user?.id);
    
    setPlantCount(count || 0);
  };

  const fetchProfile = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('display_name, phone')
      .eq('user_id', user?.id)
      .single();
    
    setProfile(data);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      toast.success('Você saiu da conta');
    } catch (error) {
      // Even if signOut fails, navigate away - user wants to leave
      console.warn('Logout error:', error);
    } finally {
      // Always navigate to home, even if signOut had issues
      navigate('/');
    }
  };

  // Detect if user is SMS user (has fake email)
  const isSmsUser = user?.email?.includes('@sms.verdeia.app') || !!user?.phone;

  // Format phone number for display: +5519920009944 -> (19) 92000-9944
  const formatPhone = (phone: string | null | undefined): string => {
    if (!phone) return '';
    // Remove +55 prefix if present
    const cleaned = phone.replace(/^\+55/, '');
    if (cleaned.length === 11) {
      const ddd = cleaned.slice(0, 2);
      const firstPart = cleaned.slice(2, 7);
      const secondPart = cleaned.slice(7);
      return `(${ddd}) ${firstPart}-${secondPart}`;
    }
    return phone;
  };

  // Get display name
  const getDisplayName = (): string => {
    if (profile?.display_name) {
      return profile.display_name;
    }
    if (isSmsUser) {
      return formatPhone(user?.phone || profile?.phone);
    }
    return user?.email?.split('@')[0] || 'Usuário';
  };

  // Get subtitle (email or phone)
  const getSubtitle = (): string | null => {
    if (isSmsUser) {
      // For SMS users, show formatted phone (don't show fake email)
      return formatPhone(user?.phone || profile?.phone);
    }
    // For email users, show email
    return user?.email || null;
  };

  const displayName = getDisplayName();
  const subtitle = getSubtitle();

  const handleNameUpdated = (newName: string) => {
    setProfile(prev => prev ? { ...prev, display_name: newName } : { display_name: newName, phone: null });
  };

  const menuItems = [
    {
      icon: Leaf,
      label: 'Minha Coleção',
      description: `${plantCount} plantas identificadas`,
      onClick: () => navigate('/collection'),
    },
    {
      icon: Camera,
      label: 'Identificar Planta',
      description: 'Use a câmera para identificar',
      onClick: () => navigate('/identify'),
    },
    ...(!isInstalled ? [{
      icon: Download,
      label: 'Instalar App',
      description: 'Adicione à tela inicial',
      onClick: () => navigate('/install'),
    }] : []),
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <MobileHeader title="Perfil" showLogo />

      <div className="px-4 py-6 space-y-6">
        {/* User Info Card */}
        <Card className="border-0 shadow-sm bg-gradient-to-br from-primary/10 to-accent/10">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="h-8 w-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <h2 className="font-display font-semibold text-lg truncate">
                    {displayName}
                  </h2>
                  {user && (
                    <EditNameDialog 
                      currentName={profile?.display_name || null} 
                      userId={user.id}
                      onNameUpdated={handleNameUpdated}
                    />
                  )}
                </div>
                {subtitle && (
                  <p className="text-sm text-muted-foreground truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Card */}
        {subscriptionLoading ? (
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded-full" />
                <Skeleton className="h-5 w-32" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-10 w-full rounded-md" />
            </CardContent>
          </Card>
        ) : (
          <SubscriptionCard
            plan={plan}
            subscriptionEnd={subscriptionEnd}
            onUpgrade={() => setShowUpgradeDialog(true)}
          />
        )}

        {/* Usage Indicator for free users */}
        {!subscriptionLoading && plan === 'free' && (
          <UsageIndicator
            plan={plan}
            monthlyUsage={monthlyUsage}
            plantCount={subPlantCount}
            maxAiUsage={limits.monthlyAiUsage}
            maxPlants={limits.maxPlants}
            onUpgradeClick={() => setShowUpgradeDialog(true)}
          />
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-leaf/10 flex items-center justify-center mx-auto mb-2">
                <Leaf className="h-5 w-5 text-leaf" />
              </div>
              <p className="text-2xl font-display font-bold text-foreground">{plantCount}</p>
              <p className="text-xs text-muted-foreground">Plantas identificadas</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
                <Camera className="h-5 w-5 text-primary" />
              </div>
              <p className="text-2xl font-display font-bold text-foreground">{monthlyUsage}</p>
              <p className="text-xs text-muted-foreground">Usos este mês</p>
            </CardContent>
          </Card>
        </div>

        {/* Notifications */}
        <NotificationToggle />

        {/* Menu Items */}
        <div className="space-y-2">
          {menuItems.map((item) => (
            <Card 
              key={item.label} 
              className="border-0 shadow-sm cursor-pointer active:scale-[0.98] transition-transform"
              onClick={item.onClick}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <item.icon className="h-5 w-5 text-secondary-foreground" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{item.label}</p>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Danger Zone */}
        <Card className="border border-destructive/20 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Zona de Perigo
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            <p className="text-sm text-muted-foreground">
              Ações irreversíveis para sua conta.
            </p>
            {user && <DeleteAccountDialog userId={user.id} />}
          </CardContent>
        </Card>

        {/* Logout Button */}
        <Button 
          variant="outline" 
          className="w-full h-12"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 h-5 w-5" />
          Sair da conta
        </Button>
      </div>

      <BottomNavigation />

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentUsage={monthlyUsage}
        maxUsage={limits.monthlyAiUsage}
        limitType="ai"
      />
    </div>
  );
}
